import { useMemo } from 'react';
import { useAccommodationsConsoleSearchParamsHelper } from '../useAccommodationsConsoleSearchParamsHelper';
import { PRIORITIZE_TAG_QUERY_PARAM_KEY } from './constants';

/**
 * Reads the curated tags an inbound link asked us to lead with:
 * `?prioritizeTag=honeymoon&prioritizeTag=luxury`.
 *
 * Both encodings are accepted — repeated keys AND the comma-delimited
 * `?prioritizeTag=honeymoon,luxury` — because a link does not necessarily arrive in
 * the shape it was written in. Every event-scoped entry point builds its URL through
 * `hotelRoutes.ts`, whose `retainSupportedStaysParams` hop re-serializes the whole
 * query string with `arrayFormat: 'comma'`; repeated keys go in and one comma-joined
 * key comes out. Reading only the repeated form would leave a multi-tag in-event link
 * matching a tag literally named "honeymoon,luxury" — no error, no results lifted,
 * exactly the silent no-op this feature already shipped once to fix.
 *
 * Splitting on commas is safe rather than ambiguous: tags are lowercase, drawn from a
 * fixed server-side vocabulary (`SUPPORTED_TAGS`, pinned by a CHECK constraint), and
 * cannot contain a comma.
 *
 * This is a context hint carried in from outside, not user-toggled filter state,
 * so it lives apart from `useFilterQueryParams`: it is read-only here, it does not
 * count toward "has active filters", and clearing the filters must not clear it.
 */
export const usePrioritizeTagsQueryParam = (): ReadonlyArray<string> => {
  const queryParamHelper = useAccommodationsConsoleSearchParamsHelper();

  return useMemo(() => {
    const rawValues = queryParamHelper.getValueArray(PRIORITIZE_TAG_QUERY_PARAM_KEY);
    if (!rawValues?.length) {
      return [];
    }

    // Normalized to match how tags are stored server-side, so a link written with
    // stray casing or spacing still lands on the right tag instead of silently
    // matching nothing.
    const normalized = rawValues.flatMap(value =>
      value
        .split(',')
        .map(tag => tag.trim().toLowerCase())
        .filter(Boolean),
    );

    return Array.from(new Set(normalized));
  }, [queryParamHelper]);
};
